import React from "react";
import { Drawer, Tag, Descriptions, Divider, Empty } from "antd";
import dayjs from "dayjs";
import type { Production } from "../../../Types";
import { DRAWER_FORM_WIDTH, drawerTitle } from "../../../components";

interface Props {
  production: Production | null;
  open: boolean;
  onClose: () => void;
}

export const ProductionDetailDrawer: React.FC<Props> = ({
  production,
  open,
  onClose,
}) => {
  if (!production) return null;

  const ingredientTotal = production.items.reduce(
    (s, i) => s + Number(i.total_cost),
    0,
  );

  return (
    <Drawer
      title={drawerTitle("Production Run Details")}
      open={open}
      onClose={onClose}
      width={DRAWER_FORM_WIDTH}
      styles={{ body: { padding: "20px 24px" } }}
      footer={null}
    >
      <Descriptions
        bordered
        column={2}
        size="small"
        className="bg-white rounded border border-gray-100"
      >
        <Descriptions.Item
          label={<span className="font-medium text-gray-500">Product</span>}
          span={2}
        >
          <span className="font-bold text-gray-800">
            {production.product_title}
          </span>
          <span className="ml-1.5 text-xs text-gray-400 font-mono">
            ({production.product_sku_code})
          </span>
        </Descriptions.Item>
        <Descriptions.Item
          label={<span className="font-medium text-gray-500">Date</span>}
        >
          <span className="text-gray-700">
            {dayjs(production.production_date).format("YYYY-MM-DD")}
          </span>
        </Descriptions.Item>
        <Descriptions.Item
          label={<span className="font-medium text-gray-500">Status</span>}
        >
          <Tag color="success" className="m-0 font-bold text-[10px] uppercase">
            {production.status}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span className="font-medium text-gray-500">Qty Produced</span>
          }
        >
          <span className="font-mono font-bold text-teal-700">
            {Number(production.quantity).toLocaleString()} units
          </span>
        </Descriptions.Item>
        <Descriptions.Item
          label={<span className="font-medium text-gray-500">Batch No</span>}
        >
          <Tag color="blue" className="m-0 font-mono text-[10px] font-semibold">
            {production.product_batch_no}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span className="font-medium text-gray-500">Additional Cost</span>
          }
        >
          <span className="font-mono font-medium text-gray-700">
            LKR{" "}
            {Number(production.additional_cost).toLocaleString("en-LK", {
              minimumFractionDigits: 2,
            })}
          </span>
        </Descriptions.Item>
        <Descriptions.Item
          label={<span className="font-medium text-gray-500">Total Cost</span>}
        >
          <span className="font-mono font-bold text-teal-700">
            LKR{" "}
            {Number(production.total_cost).toLocaleString("en-LK", {
              minimumFractionDigits: 2,
            })}
          </span>
        </Descriptions.Item>
        {production.creator_name && (
          <Descriptions.Item
            label={
              <span className="font-medium text-gray-500">Recorded By</span>
            }
            span={2}
          >
            <span className="text-gray-700 font-medium">
              {production.creator_name}
            </span>
          </Descriptions.Item>
        )}
        {production.note && (
          <Descriptions.Item
            label={<span className="font-medium text-gray-500">Note</span>}
            span={2}
          >
            <span className="text-gray-600 block leading-relaxed">
              {production.note}
            </span>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider className="my-4" />

      <div className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-400">
        Raw Materials Consumed
      </div>

      {production.items?.length > 0 ? (
        <div className="overflow-hidden rounded border border-gray-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-500 border-b border-gray-200">
                <th className="px-3 py-2 text-left font-semibold">
                  Ingredient
                </th>
                <th className="px-3 py-2 text-right font-semibold">Qty</th>
                <th className="px-3 py-2 text-right font-semibold">Wastage</th>
                <th className="px-3 py-2 text-right font-semibold">Cost</th>
              </tr>
            </thead>
            <tbody>
              {production.items.map((item, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-3 py-2">
                    <div className="font-bold text-gray-700">
                      {item.raw_material_name}
                    </div>
                    <div className="text-[10px] text-gray-400 font-mono">
                      {item.raw_material_batch_no}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-gray-600">
                    {Number(item.quantity).toFixed(3)} {item.raw_material_unit}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-amber-500">
                    {Number(item.wastage_quantity).toFixed(3)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-gray-700">
                    {Number(item.total_cost).toLocaleString("en-LK", {
                      minimumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-gray-200 bg-gray-50">
              <tr>
                <td
                  colSpan={3}
                  className="px-3 py-2 text-right font-medium text-gray-500"
                >
                  Ingredients Subtotal
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold text-gray-700">
                  {ingredientTotal.toLocaleString("en-LK", {
                    minimumFractionDigits: 2,
                  })}
                </td>
              </tr>
              {production.additional_cost > 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-2 text-right font-medium text-gray-500"
                  >
                    Additional Cost
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-gray-700">
                    {Number(production.additional_cost).toLocaleString(
                      "en-LK",
                      { minimumFractionDigits: 2 },
                    )}
                  </td>
                </tr>
              )}
              <tr className="border-t border-gray-300 bg-teal-50/50">
                <td
                  colSpan={3}
                  className="px-3 py-2.5 text-right font-bold text-teal-700"
                >
                  Grand Total
                </td>
                <td className="px-3 py-2.5 text-right font-mono font-bold text-teal-700 text-sm">
                  LKR{" "}
                  {Number(production.total_cost).toLocaleString("en-LK", {
                    minimumFractionDigits: 2,
                  })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <Empty
          description="No ingredient details available."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </Drawer>
  );
};
